import createClient, { type Client, type Middleware } from 'openapi-fetch';
import type { components, paths } from './generated/schema';

export type { components, operations, paths } from './generated/schema';

/** Typed client for the ARG Zombie API. */
export type ApiClient = Client<paths>;

/** Shorthand for the API's schema types, e.g. `Schemas['VersionResponse']`. */
export type Schemas = components['schemas'];

/** Returns the current access token, or null/undefined when signed out. */
export type TokenProvider = () => string | null | undefined | Promise<string | null | undefined>;

/** Middleware that adds `Authorization: Bearer <token>` when a token is available. */
export function bearerAuthMiddleware(getToken: TokenProvider): Middleware {
  return {
    async onRequest({ request }) {
      const token = await getToken();
      if (token) {
        request.headers.set('Authorization', `Bearer ${token}`);
      }
      return request;
    },
  };
}

/**
 * Creates a typed `openapi-fetch` client for the API.
 * @param baseUrl API origin, e.g. `http://localhost:5080` or `https://api.dev.argzombie.com`.
 * @param getToken optional access-token provider. When given, requests carry a bearer token.
 * @param options.fetch optional fetch (e.g. one that refreshes the session and retries after a 401).
 */
export function createApiClient(
  baseUrl: string,
  getToken?: TokenProvider,
  options?: { fetch?: (request: Request) => Promise<Response> },
): ApiClient {
  const client = createClient<paths>({
    baseUrl: baseUrl.replace(/\/+$/, ''),
    fetch: options?.fetch,
  });
  if (getToken) {
    client.use(bearerAuthMiddleware(getToken));
  }
  return client;
}
